import React, { useEffect, useState, useMemo, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { $generateHtmlFromNodes } from "@lexical/html";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import {
  getKnowledgeRootFolders,
  getKnowledgeChildFolders,
  getKnowledgeNotes,
  createKnowledgeFolder,
  createKnowledgeNote,
  updateKnowledgeNote,
  deleteKnowledgeNote,
  deleteKnowledgeFolder,
  pinKnowledgeNote,
  unpinKnowledgeNote,
  getKnowledgePins,
  getKnowledgeFolderById,
  updateKnowledgeFolder,
} from "../api";
import { useTheme } from "../context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";

const KnowledgeHub = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [rootFolders, setRootFolders] = useState([]);
  const [childFolders, setChildFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [notes, setNotes] = useState([]);
  const [pinnedNoteIds, setPinnedNoteIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedNoteIds, setExpandedNoteIds] = useState(() => new Set());

  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderVisibility, setNewFolderVisibility] = useState("private");

  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContentHtml, setNewNoteContentHtml] = useState("");
  const [newNoteTags, setNewNoteTags] = useState("");

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareVisibility, setShareVisibility] = useState("private");
  const [shareEmails, setShareEmails] = useState("");

  const [editingNote, setEditingNote] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContentHtml, setEditContentHtml] = useState("");
  const [editTags, setEditTags] = useState("");
  const editContentRef = useRef(null);
  const [copiedNoteId, setCopiedNoteId] = useState(null);
  const [highlightedNoteId, setHighlightedNoteId] = useState(null);
  const hasScrolledToDeepLinkRef = useRef(false);
  const [togglingPinNoteId, setTogglingPinNoteId] = useState(null);
  const [showSubfolderForm, setShowSubfolderForm] = useState(false);
  const [subfolderName, setSubfolderName] = useState("");
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const deepLinkFolderId = searchParams.get("folderId");
  const deepLinkNoteId = searchParams.get("noteId");

  const editorConfig = useMemo(
    () => ({
      namespace: "knowledge-hub-note",
      theme: {},
      onError: (error) => {
        console.error("Lexical error:", error);
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    }),
    []
  );

  const loadRootFolders = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getKnowledgeRootFolders();
      setRootFolders(res.data.data || []);
    } catch (e) {
      console.error("Failed to load folders", e);
      setError("Failed to load folders");
    } finally {
      setLoading(false);
    }
  };

  const loadChildFolders = async (folderId) => {
    try {
      const res = await getKnowledgeChildFolders(folderId);
      setChildFolders(res.data.data || []);
    } catch (e) {
      console.error("Failed to load child folders", e);
    }
  };

  const loadNotes = async (folderId, pinnedIdsOverride = null) => {
    try {
      setLoading(true);
      setError("");
      const res = await getKnowledgeNotes(folderId);
      const data = res.data.data || [];
      const pinnedIds = pinnedIdsOverride !== null ? pinnedIdsOverride : pinnedNoteIds;
      const withPinned = data.map((n) => ({
        ...n,
        isPinned: pinnedIds.includes(n.id),
      }));
      setNotes(withPinned);
    } catch (e) {
      console.error("Failed to load notes", e);
      setError("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  const loadPins = async () => {
    try {
      const res = await getKnowledgePins();
      const items = res.data.data || [];
      setPinnedNoteIds(items.map((p) => p.note_id));
    } catch (e) {
      console.error("Failed to load pins", e);
    }
  };

  useEffect(() => {
    (async () => {
      await loadRootFolders();
      await loadPins();

      if (deepLinkFolderId) {
        try {
          const folderRes = await getKnowledgeFolderById(deepLinkFolderId);
          const folder = folderRes.data.data;
          if (folder) {
            await handleSelectFolder(folder, { skipUrlUpdate: true });
          }
        } catch (e) {
          console.error("Failed to open deep-linked folder", e);
        }
      }
    })();
  }, [deepLinkFolderId, navigate]);

  useEffect(() => {
    if (!deepLinkNoteId || notes.length === 0 || hasScrolledToDeepLinkRef.current) return;
    const noteExists = notes.some((n) => n.id === deepLinkNoteId);
    if (!noteExists) return;

    setExpandedNoteIds((prev) => new Set(prev).add(deepLinkNoteId));
    setHighlightedNoteId(deepLinkNoteId);
    hasScrolledToDeepLinkRef.current = true;

    const scrollAndHighlight = () => {
      const el = document.querySelector(`[data-note-id="${deepLinkNoteId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    const t1 = setTimeout(scrollAndHighlight, 200);
    const t2 = setTimeout(() => setHighlightedNoteId(null), 2800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [deepLinkNoteId, notes]);

  const handleSelectFolder = async (folder, options = {}) => {
    setSelectedFolder(folder);
    setChildFolders([]);
    setNotes([]);
    await loadChildFolders(folder.id);
    await loadNotes(folder.id);

    if (!options.skipUrlUpdate) {
      const params = new URLSearchParams();
      params.set("folderId", folder.id);
      navigate(`/knowledge-hub?${params.toString()}`, { replace: true });
    }
  };

  const handleCreateFolder = async (parentId = null, nameOverride = null) => {
    const name = (nameOverride ?? newFolderName).trim();
    if (!name) return;
    try {
      setLoading(true);
      setError("");
      await createKnowledgeFolder({
        name,
        parentId,
        visibility: newFolderVisibility,
        sharedWith: [],
      });
      setNewFolderName("");
      if (nameOverride) {
        setSubfolderName("");
        setShowSubfolderForm(false);
      }
      if (!parentId) {
        setShowCreateFolderModal(false);
      }
      await loadRootFolders();
      if (selectedFolder && parentId === selectedFolder.id) {
        await loadChildFolders(selectedFolder.id);
      }
    } catch (e) {
      console.error("Failed to create folder", e);
      setError("Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  const closeCreateFolderModal = () => {
    setShowCreateFolderModal(false);
    setNewFolderName("");
  };

  const handleCreateSubfolder = async () => {
    if (!selectedFolder || !subfolderName.trim()) return;
    await handleCreateFolder(selectedFolder.id, subfolderName.trim());
  };

  const handleCreateNote = async () => {
    if (!selectedFolder || !newNoteTitle.trim()) return;
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      await createKnowledgeNote(selectedFolder.id, {
        type: "note",
        title: newNoteTitle.trim(),
        contentHtml: newNoteContentHtml,
        tags: newNoteTags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setNewNoteTitle("");
      setNewNoteContentHtml("");
      setNewNoteTags("");
      await loadPins();
      await loadNotes(selectedFolder.id);
      setSuccessMessage("Note added.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (e) {
      console.error("Failed to create note", e);
      setError("Failed to create note");
    } finally {
      setLoading(false);
    }
  };

  const toggleNoteExpanded = (noteId) => {
    setExpandedNoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const getTextPreview = (html, maxLen = 120) => {
    if (!html) return "";
    const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return text.length <= maxLen ? text : text.slice(0, maxLen) + "…";
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedFolder) return;
    try {
      await deleteKnowledgeNote(noteId);
      await loadPins();
      await loadNotes(selectedFolder.id);
    } catch (e) {
      console.error("Failed to delete note", e);
      setError("Failed to delete note");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteKnowledgeFolder(folderId);
      if (selectedFolder && selectedFolder.id === folderId) {
        setSelectedFolder(null);
        setNotes([]);
      }
      await loadRootFolders();
    } catch (e) {
      console.error("Failed to delete folder", e);
      setError("Failed to delete folder (ensure it has no subfolders or notes)");
    }
  };

  const handleTogglePin = async (note) => {
    if (!selectedFolder) return;
    setTogglingPinNoteId(note.id);
    try {
      if (note.isPinned) {
        await unpinKnowledgeNote(note.id);
      } else {
        await pinKnowledgeNote(note.id);
      }
      const pinsRes = await getKnowledgePins();
      const newPinnedIds = (pinsRes.data.data || []).map((p) => p.note_id);
      setPinnedNoteIds(newPinnedIds);
      await loadNotes(selectedFolder.id, newPinnedIds);
    } catch (e) {
      console.error("Failed to toggle pin", e);
      setError(note.isPinned ? "Failed to unpin" : "Failed to pin");
    } finally {
      setTogglingPinNoteId(null);
    }
  };

  const openShareModal = () => {
    if (!selectedFolder) return;
    setShareVisibility(selectedFolder.visibility || "private");
    const emails = Array.isArray(selectedFolder.shared_with)
      ? selectedFolder.shared_with.join(", ")
      : "";
    setShareEmails(emails);
    setShareModalOpen(true);
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setEditTitle(note.title || "");
    setEditContentHtml(note.content_html || "");
    setEditTags(Array.isArray(note.tags) ? note.tags.join(", ") : "");
  };

  useEffect(() => {
    if (editingNote && editContentRef.current) {
      editContentRef.current.innerHTML = editingNote.content_html || "";
    }
  }, [editingNote]);

  const closeEditModal = () => {
    setEditingNote(null);
    setEditTitle("");
    setEditContentHtml("");
    setEditTags("");
  };

  const handleCopyNoteLink = (note) => {
    if (!selectedFolder) return;
    const url = `${window.location.origin}/knowledge-hub?folderId=${encodeURIComponent(selectedFolder.id)}&noteId=${encodeURIComponent(note.id)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedNoteId(note.id);
      setTimeout(() => setCopiedNoteId(null), 2000);
    }).catch(() => setError("Failed to copy link"));
  };

  const handleSaveEdit = async () => {
    if (!editingNote || !selectedFolder) return;
    try {
      setError("");
      const html = editContentRef.current ? editContentRef.current.innerHTML : editContentHtml;
      await updateKnowledgeNote(editingNote.id, {
        title: editTitle.trim(),
        contentHtml: html,
        tags: editTags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setSuccessMessage("Note updated.");
      setTimeout(() => setSuccessMessage(""), 3000);
      closeEditModal();
      await loadNotes(selectedFolder.id);
    } catch (e) {
      console.error("Failed to update note", e);
      setError("Failed to update note");
    }
  };

  const handleSaveShare = async () => {
    if (!selectedFolder) return;
    try {
      const emails = shareEmails
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      await updateKnowledgeFolder(selectedFolder.id, {
        visibility: shareVisibility,
        sharedWith: emails,
      });
      // Reload folder data and root list
      const folderRes = await getKnowledgeFolderById(selectedFolder.id);
      const folder = folderRes.data.data;
      setSelectedFolder(folder);
      await loadRootFolders();
      setShareModalOpen(false);
    } catch (e) {
      console.error("Failed to update sharing", e);
      setError("Failed to update sharing");
    }
  };

  return (
    <div
      style={{
        padding: "32px 24px",
        maxWidth: "1100px",
        margin: "0 auto",
        color: theme.text,
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" }}>
          Knowledge Hub
        </h1>
        <p style={{ margin: "6px 0 0", color: theme.textSecondary, fontSize: 14, lineHeight: 1.4 }}>
          Organize internal knowledge into folders, control visibility, and pin
          important notes for quick access.
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 8,
            backgroundColor: "rgba(200, 50, 50, 0.1)",
            color: "#c92a2a",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {successMessage && (
        <div
          style={{
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 8,
            backgroundColor: "rgba(40, 167, 69, 0.12)",
            color: "#2b8a3e",
            fontSize: 13,
          }}
        >
          {successMessage}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px 1fr",
          gap: "24px",
          alignItems: "flex-start",
        }}
      >
        {/* Folders column */}
        <div
          style={{
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 20,
            backgroundColor: theme.cardBackground,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15, fontWeight: 600 }}>Folders</h3>

          <button
            type="button"
            onClick={() => setShowCreateFolderModal(true)}
            style={{
              width: "100%",
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.backgroundSecondary,
              color: theme.text,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            New folder
          </button>

          <div style={{ maxHeight: 360, overflowY: "auto", fontSize: 14 }}>
            {rootFolders.length === 0 && (
              <div style={{ color: theme.textSecondary, fontSize: 13 }}>
                No folders yet. Click &quot;New folder&quot; to create one.
              </div>
            )}
            {rootFolders.map((folder) => (
              <React.Fragment key={folder.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 4px",
                    borderRadius: 4,
                    backgroundColor:
                      selectedFolder && selectedFolder.id === folder.id
                        ? theme.tableRowHover
                        : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSelectFolder(folder)}
                >
                  <div>
                    <div>{folder.name}</div>
                    <div
                      style={{
                        fontSize: 11,
                        color: theme.textSecondary,
                      }}
                    >
                      {folder.visibility || "private"}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder.id);
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#c92a2a",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    ✕
                  </button>
                </div>
                {selectedFolder &&
                  selectedFolder.id === folder.id &&
                  childFolders.length > 0 && (
                    <div style={{ marginLeft: 16 }}>
                      {childFolders.map((child) => (
                        <div
                          key={child.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "4px 4px",
                            borderRadius: 4,
                            backgroundColor:
                              selectedFolder && selectedFolder.id === child.id
                                ? theme.tableRowHover
                                : "transparent",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSelectFolder(child)}
                        >
                          <div style={{ fontSize: 13 }}>↳ {child.name}</div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(child.id);
                            }}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c92a2a",
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Notes column */}
        <div
          style={{
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 20,
            backgroundColor: theme.cardBackground,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>
              {selectedFolder ? selectedFolder.name : "Select a folder"}
            </h3>
            {selectedFolder && (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                {!showSubfolderForm ? (
                  <button
                    onClick={() => setShowSubfolderForm(true)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      border: `1px solid ${theme.border}`,
                      backgroundColor: "transparent",
                      color: theme.text,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    New subfolder
                  </button>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="text"
                      placeholder="Subfolder name"
                      value={subfolderName}
                      onChange={(e) => setSubfolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateSubfolder();
                        if (e.key === "Escape") setShowSubfolderForm(false);
                      }}
                      autoFocus
                      style={{
                        width: 160,
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: `1px solid ${theme.inputBorder}`,
                        backgroundColor: theme.inputBackground,
                        color: theme.text,
                        fontSize: 12,
                      }}
                    />
                    <button
                      onClick={handleCreateSubfolder}
                      disabled={loading || !subfolderName.trim()}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "none",
                        backgroundColor: theme.primary,
                        color: "#fff",
                        fontSize: 12,
                        cursor: loading ? "default" : "pointer",
                      }}
                    >
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowSubfolderForm(false); setSubfolderName(""); }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: `1px solid ${theme.border}`,
                        backgroundColor: "transparent",
                        color: theme.text,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <button
                  onClick={openShareModal}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 4,
                    border: `1px solid ${theme.border}`,
                    backgroundColor: "transparent",
                    color: theme.text,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Share
                </button>
              </div>
            )}
          </div>

          {selectedFolder ? (
            <>
              <div
                style={{
                  marginBottom: 12,
                  padding: 10,
                  borderRadius: 4,
                  backgroundColor: theme.backgroundSecondary,
                }}
              >
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  Create a quick note in this folder:
                </div>
                <input
                  type="text"
                  placeholder="Note title"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 6,
                    padding: "6px 8px",
                    borderRadius: 4,
                    border: `1px solid ${theme.inputBorder}`,
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    fontSize: 13,
                  }}
                />
                <div
                  style={{
                    marginBottom: 6,
                    borderRadius: 4,
                    border: `1px solid ${theme.inputBorder}`,
                    backgroundColor: theme.inputBackground,
                  }}
                >
                  <LexicalComposer initialConfig={editorConfig}>
                    <div style={{ padding: 8 }}>
                      <RichTextPlugin
                        contentEditable={
                          <ContentEditable
                            style={{
                              minHeight: 80,
                              maxHeight: 240,
                              overflowY: "auto",
                              outline: "none",
                              color: theme.text,
                              fontSize: 13,
                            }}
                          />
                        }
                      />
                      <HistoryPlugin />
                      <OnChangePlugin
                        onChange={(editorState, editor) => {
                          editorState.read(() => {
                            const html = $generateHtmlFromNodes(editor);
                            setNewNoteContentHtml(html);
                          });
                        }}
                      />
                    </div>
                  </LexicalComposer>
                </div>
                <input
                  type="text"
                  placeholder="Tags (comma separated)"
                  value={newNoteTags}
                  onChange={(e) => setNewNoteTags(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 6,
                    padding: "6px 8px",
                    borderRadius: 4,
                    border: `1px solid ${theme.inputBorder}`,
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    fontSize: 13,
                  }}
                />
                <button
                  onClick={handleCreateNote}
                  disabled={loading || !newNoteTitle.trim()}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 4,
                    border: "none",
                    backgroundColor: theme.primary,
                    color: "#fff",
                    fontSize: 13,
                    cursor: loading ? "default" : "pointer",
                  }}
                >
                  {loading ? "Adding…" : "Add note"}
                </button>
              </div>

              <div style={{ fontSize: 14 }}>
                {loading && notes.length === 0 ? (
                  <div style={{ color: theme.textSecondary }}>Loading…</div>
                ) : notes.length === 0 ? (
                  <div style={{ color: theme.textSecondary }}>
                    No notes yet. Create one above.
                  </div>
                ) : (
                  notes.map((note) => {
                    const isExpanded = expandedNoteIds.has(note.id);
                    const hasContent = note.content_html && note.content_html.trim();
                    const preview = getTextPreview(note.content_html);
                    return (
                      <div
                        key={note.id}
                        data-note-id={note.id}
                        style={{
                          borderBottom: `1px solid ${theme.borderLight}`,
                          padding: "8px 0",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          backgroundColor: highlightedNoteId === note.id ? "rgba(0, 123, 255, 0.12)" : "transparent",
                          borderRadius: 6,
                          marginLeft: highlightedNoteId === note.id ? -4 : 0,
                          marginRight: highlightedNoteId === note.id ? -4 : 0,
                          paddingLeft: highlightedNoteId === note.id ? 12 : 8,
                          paddingRight: highlightedNoteId === note.id ? 12 : 8,
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{note.title}</div>
                          {Array.isArray(note.tags) && note.tags.length > 0 && (
                            <div
                              style={{
                                marginTop: 4,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 4,
                              }}
                            >
                              {note.tags.map((tag) => (
                                <span
                                  key={tag}
                                  style={{
                                    fontSize: 11,
                                    padding: "2px 6px",
                                    borderRadius: 999,
                                    backgroundColor: theme.tableRowHover,
                                    color: theme.textSecondary,
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {hasContent && (
                            <>
                              {isExpanded ? (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: theme.textSecondary,
                                    marginTop: 4,
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: note.content_html,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: theme.textSecondary,
                                    marginTop: 4,
                                  }}
                                >
                                  {preview}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => toggleNoteExpanded(note.id)}
                                style={{
                                  marginTop: 4,
                                  padding: 0,
                                  border: "none",
                                  background: "none",
                                  color: theme.primary,
                                  fontSize: 12,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                {isExpanded ? "Show less" : "Show more"}
                              </button>
                            </>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 4,
                            minWidth: 90,
                            flexShrink: 0,
                          }}
                        >
                          <button
                            onClick={() => handleTogglePin(note)}
                            disabled={togglingPinNoteId === note.id}
                            title={note.isPinned ? "Unpin" : "Pin"}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: note.isPinned ? theme.primary : theme.textSecondary,
                              cursor: togglingPinNoteId === note.id ? "wait" : "pointer",
                              fontSize: 14,
                              fontWeight: 500,
                              opacity: togglingPinNoteId === note.id ? 0.7 : 1,
                            }}
                          >
                            {togglingPinNoteId === note.id ? "…" : note.isPinned ? "Unpin" : "📌"}
                          </button>
                          <button
                            onClick={() => handleCopyNoteLink(note)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: theme.textSecondary,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                            title="Copy link to this note"
                          >
                            {copiedNoteId === note.id ? "Copied!" : "Copy link"}
                          </button>
                          <button
                            onClick={() => openEditModal(note)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: theme.primary,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c92a2a",
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div style={{ color: theme.textSecondary, fontSize: 14 }}>
              Select a folder from the left to view and add notes.
            </div>
          )}
        </div>

      {showCreateFolderModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: 400,
              maxWidth: "90vw",
              backgroundColor: theme.cardBackground,
              borderRadius: 8,
              padding: 16,
              border: `1px solid ${theme.border}`,
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>New folder</h3>
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Folder name</label>
            <input
              type="text"
              placeholder="New folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "6px 8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
              }}
            />
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Visibility</label>
            <select
              value={newFolderVisibility}
              onChange={(e) => setNewFolderVisibility(e.target.value)}
              style={{
                width: "100%",
                marginBottom: 12,
                padding: "6px 8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
              }}
            >
              <option value="private">Private</option>
              <option value="shared">Shared (specific people)</option>
              <option value="org">Org-wide</option>
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={closeCreateFolderModal}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  backgroundColor: "transparent",
                  color: theme.text,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleCreateFolder(null)}
                disabled={loading || !newFolderName.trim()}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "none",
                  backgroundColor: theme.primary,
                  color: "#fff",
                  fontSize: 13,
                  cursor: loading ? "default" : "pointer",
                }}
              >
                {loading ? "Creating…" : "Create folder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: 420,
              maxWidth: "90vw",
              backgroundColor: theme.cardBackground,
              borderRadius: 8,
              padding: 16,
              border: `1px solid ${theme.border}`,
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Share folder</h3>
            <p style={{ marginTop: 0, fontSize: 13, color: theme.textSecondary }}>
              Control who can see and edit this folder. For org-wide, everyone
              in HP Admin will have access.
            </p>
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
              Visibility
            </label>
            <select
              value={shareVisibility}
              onChange={(e) => setShareVisibility(e.target.value)}
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "6px 8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
              }}
            >
              <option value="private">Private (owner + specific emails)</option>
              <option value="shared">Shared (specific emails only)</option>
              <option value="org">Org-wide (everyone)</option>
            </select>

            {(shareVisibility === "private" || shareVisibility === "shared") && (
              <>
                <label
                  style={{ display: "block", fontSize: 13, marginBottom: 4 }}
                >
                  Shared with (emails, comma separated)
                </label>
                <textarea
                  rows={2}
                  value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  placeholder="user1@highperformr.ai, user2@highperformr.ai"
                  style={{
                    width: "100%",
                    marginBottom: 10,
                    padding: "6px 8px",
                    borderRadius: 4,
                    border: `1px solid ${theme.inputBorder}`,
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    fontSize: 13,
                    resize: "vertical",
                  }}
                />
              </>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 8,
              }}
            >
              <button
                onClick={() => setShareModalOpen(false)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  backgroundColor: "transparent",
                  color: theme.text,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveShare}
                style={{
                  padding: "6px 10px",
                  borderRadius: 4,
                  border: "none",
                  backgroundColor: theme.primary,
                  color: "#fff",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editingNote && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: 560,
              maxWidth: "90vw",
              maxHeight: "85vh",
              backgroundColor: theme.cardBackground,
              borderRadius: 8,
              padding: 16,
              border: `1px solid ${theme.border}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Edit note</h3>
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Title</label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "6px 8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
              }}
            />
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Content</label>
            <div
              ref={editContentRef}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setEditContentHtml(e.target.innerHTML)}
              style={{
                minHeight: 120,
                maxHeight: 280,
                overflowY: "auto",
                marginBottom: 10,
                padding: "8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
                outline: "none",
              }}
            />
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Tags (comma separated)</label>
            <input
              type="text"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              placeholder="tag1, tag2"
              style={{
                width: "100%",
                marginBottom: 12,
                padding: "6px 8px",
                borderRadius: 4,
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
                fontSize: 13,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={closeEditModal}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  backgroundColor: "transparent",
                  color: theme.text,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "none",
                  backgroundColor: theme.primary,
                  color: "#fff",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default KnowledgeHub;

